import React from "react";
import { getCompanyConfig } from "@/config/company";
import { PrintDocumentTitle } from "@/components/shared/print";

export default function CostBreakdownPrintTemplate({ data, config }: { data: any, config: any }) {
    // data.items is the array of MOQ calculations
    const moqs = data.items || [];
    const c = getCompanyConfig(config);
    
    const formatCurrency = (val: number) => new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(val);

    return (
        <div className="w-full bg-white print:bg-white text-black p-[10mm] font-serif text-[13px] leading-tight print:break-inside-avoid">
            <style type="text/css" media="print">
                {`@page { size: A4 landscape; margin: 0; }`}
            </style>
            {/* ========== TOP HEADER (ISO 9001:2015) ========== */}
            <div className="flex justify-between items-start mb-6 gap-4">
                {/* Left: Logo */}
                <div className="w-[30%] flex items-start">
                    <img src={c.logoUrl} alt="Logo" className="w-[120px] h-auto max-h-[80px] object-contain flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>

                {/* Right: Company Info */}
                <div className="w-[70%] flex flex-col items-end text-right mt-2">
                    <div className="font-bold text-[13px] uppercase leading-tight mb-1 text-slate-800">{c.companyName}</div>
                    <div className="text-[10px] text-slate-600 uppercase font-medium">{c.companyNameEn || ''}</div>
                </div>
            </div>

            <PrintDocumentTitle
                title="BẢNG PHÂN TÍCH GIÁ THÀNH"
                subtitle="COST BREAKDOWN ANALYSIS"
                extraMeta={data.productName ? <span>Sản phẩm: <strong className="text-black font-bold">{data.productName}</strong></span> : null}
            />


            <table className="w-full border-collapse border border-slate-400 mt-4 text-sm">
                <thead>
                    <tr className="bg-slate-100 print:bg-slate-100 font-bold print:text-black">
                        <td className="border border-slate-400 p-2 text-center w-12">STT</td>
                        <td className="border border-slate-400 p-2">Hạng mục chi phí</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {moq.quantity.toLocaleString()} SP
                            </td>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {/* ========== VARIABLE COSTS ========== */}
                    <tr className="bg-slate-50 print:bg-slate-50 font-bold italic">
                        <td className="border border-slate-400 p-2 text-center text-slate-700 print:text-black">I</td>
                        <td className="border border-slate-400 p-2 text-slate-700 print:text-black" colSpan={moqs.length + 1}>Chi phí Biến đổi (Variable Costs)</td>
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">1</td>
                        <td className="border border-slate-400 p-2">Chi phí Giấy</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.variableCosts?.paperCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">2</td>
                        <td className="border border-slate-400 p-2">Chi phí Công in</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.variableCosts?.pressCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">3</td>
                        <td className="border border-slate-400 p-2">Chi phí Thành phẩm</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.variableCosts?.postPressCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-semibold bg-gray-50 print:bg-gray-50">
                        <td className="border border-slate-400 p-2 text-center"></td>
                        <td className="border border-slate-400 p-2 text-right">Cộng Chi phí Biến đổi:</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right text-slate-700 print:text-black">
                                {formatCurrency(moq.variableCosts?.total || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-semibold text-xs text-slate-600 print:text-black">
                        <td className="border border-slate-400 p-1 text-center"></td>
                        <td className="border border-slate-400 p-1 text-right italic">Phân bổ / SP:</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-1 text-right italic">
                                {formatCurrency(moq.variableCosts?.unitAllocation || 0)}
                            </td>
                        ))}
                    </tr>

                    {/* ========== FIXED COSTS ========== */}
                    <tr className="bg-slate-50 print:bg-slate-50 font-bold italic">
                        <td className="border border-slate-400 p-2 text-center text-slate-700 print:text-black">II</td>
                        <td className="border border-slate-400 p-2 text-slate-700 print:text-black" colSpan={moqs.length + 1}>Chi phí Cố định (Fixed Costs)</td>
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">4</td>
                        <td className="border border-slate-400 p-2">Chi phí Kẽm</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.fixedCosts?.plateCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">5</td>
                        <td className="border border-slate-400 p-2">Chi phí Khuôn</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.fixedCosts?.moldCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-semibold bg-gray-50 print:bg-gray-50">
                        <td className="border border-slate-400 p-2 text-center"></td>
                        <td className="border border-slate-400 p-2 text-right">Cộng Chi phí Cố định (Tổng):</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right text-slate-700 print:text-black">
                                {formatCurrency(moq.fixedCosts?.total || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-semibold text-xs text-slate-600 print:text-black">
                        <td className="border border-slate-400 p-1 text-center"></td>
                        <td className="border border-slate-400 p-1 text-right italic">Phân bổ / SP:</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-1 text-right italic">
                                {formatCurrency(moq.fixedCosts?.unitAllocation || 0)}
                            </td>
                        ))}
                    </tr>

                    {/* ========== SUMMARY ========== */}
                    <tr className="font-bold bg-slate-100 print:bg-slate-100">
                        <td className="border border-slate-400 p-2 text-center">III</td>
                        <td className="border border-slate-400 p-2 uppercase">Tổng Chi Phí SX (I + II)</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.summary?.totalCost || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td className="border border-slate-400 p-2 text-center">6</td>
                        <td className="border border-slate-400 p-2">Lợi nhuận dự kiến ({moqs[0]?.summary?.marginPercent || 0}%)</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right">
                                {formatCurrency(moq.summary?.marginValue || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-bold bg-blue-50 print:bg-blue-50">
                        <td className="border border-slate-400 p-2 text-center"></td>
                        <td className="border border-slate-400 p-2 uppercase">Giá bán tổng</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right text-indigo-700 print:text-black">
                                {formatCurrency(moq.summary?.totalPrice || 0)}
                            </td>
                        ))}
                    </tr>
                    <tr className="font-bold bg-yellow-50 print:bg-yellow-50">
                        <td className="border border-slate-400 p-2 text-center"></td>
                        <td className="border border-slate-400 p-2 uppercase text-red-600 print:text-black">Đơn giá / SP</td>
                        {moqs.map((moq: any) => (
                            <td key={moq.quantity} className="border border-slate-400 p-2 text-right text-red-600 print:text-black text-base">
                                {formatCurrency(moq.summary?.unitPrice || 0)}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>

            <div className="grid grid-cols-2 mt-12 gap-8 text-center font-bold">
                <div>Người Lập Bảng</div>
                <div>Phê Duyệt</div>
            </div>
            
            {/* Spacer for signature */}
            <div className="h-24"></div>
        </div>
    );
}
